import { Theme } from "@emotion/react";
import { createTheme } from "@mui/material";
import {
  borderGrey,
  borderOrange,
  borderStyle,
  boxHeight,
  defaultTransition,
  mediumTextFieldHeight,
  perelynBlack,
  perelynDarkGrey,
  perelynGrey,
  perelynPrimary,
  smallGap,
  tinyGap,
} from "./styles/consts";

declare module "@mui/material/styles" {
  interface Palette {
    grey: Palette["grey"];
  }

  interface PaletteOptions {
    grey?: PaletteOptions["grey"];
  }

  interface TypographyVariants {
    button: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    button?: React.CSSProperties;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    grey: true;
  }
  interface ButtonPropsVariantOverrides {
    primary: true;
    outlined: true;
    transparentBig: true;
    orangeBig: true;
    transparentSmall: true;
    navigation: true;
    orange: true;
  }
  interface TextFieldPropsColorOverrides {
    grey: true;
  }
}

export const theme: Theme = createTheme({
  palette: {
    text: {
      primary: perelynBlack,
      secondary: perelynPrimary,
    },
    primary: {
      main: perelynPrimary,
      light: "#ffffff",
      dark: perelynBlack,
      contrastText: perelynBlack,
    },
    secondary: {
      main: "#ffffff",
      dark: perelynPrimary,
      contrastText: perelynBlack,
    },
    error: {
      main: "#F97066",
      600: "#D92D20",
    },
    success: {
      main: "#32D583",
    },
  },
  typography: {
    fontFamily: "ABCWhyte",
    button: {
      textTransform: "none",
    },
  },
  components: {
    MuiStack: {
      styleOverrides: {
        root: {
          input: {},
          "*::-webkit-scrollbar": {
            width: tinyGap,
          },

          "*::-webkit-scrollbar-thumb": {
            backgroundColor: "#ffffff",
            border: borderStyle,
          },
          fontSize: "0.875rem",
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: "#242424",
          fontSize: "0.875rem",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: "0px",
          boxShadow: "none",
          height: "2.25rem",
          cursor: "crosshair",
          whiteSpace: "nowrap",
          transition: defaultTransition,
          color: perelynBlack,
          padding: `${tinyGap} ${smallGap}`,
          textTransform: "none",
          fontWeight: 400,
          fontSize: "0.875rem",
          lineHeight: 1.5,
          "&:hover": {
            boxShadow: "none",
          },
        },
        contained: {
          backgroundColor: "#ffffff",
          border: "none",
          "&:hover": {
            backgroundColor: perelynPrimary,
          },
          "&:disabled": {
            backgroundColor: "#ffffff",
            color: perelynGrey,
            border: borderGrey,
          },
        },
        outlined: {
          backgroundColor: "transparent",
          border: borderStyle,
          "&:hover": {
            backgroundColor: perelynPrimary,
            border: borderStyle,
            "& > *": {
              transition: defaultTransition,
              filter:
                "brightness(0) saturate(100%) invert(11%) sepia(0%) saturate(0%) hue-rotate(131deg) brightness(92%) contrast(90%)",
            },
          },
        },
        text: {
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "transparent",
          },
        },
      },
      variants: [
        {
          props: { variant: "primary" },
          style: {
            color: perelynBlack,
            backgroundColor: perelynPrimary,
            border: borderStyle,
            justifyContent: "center",
            width: "fit-content",
            "&:hover": {
              color: perelynPrimary,
              backgroundColor: perelynBlack,
            },
            "&:disabled": {
              backgroundColor: perelynGrey,
              color: perelynDarkGrey,
              border: borderStyle,
            },
          },
        },
        {
          props: { variant: "transparentBig" },
          style: {
            color: perelynBlack,
            backgroundColor: "transparent",
            justifyContent: "center",
            flexDirection: "row",
            alignItems: "center",
            boxSizing: "border-box",
            height: boxHeight,
            cursor: "crosshair",
            transition: "background-color 0.4s, padding .3s",
            "&:hover": {
              backgroundColor: perelynPrimary,
              "& > *": { cursor: "crosshair" },
            },
          },
        },
        {
          props: { variant: "orangeBig" },
          style: {
            color: perelynBlack,
            backgroundColor: perelynPrimary,
            justifyContent: "center",
            flexDirection: "row",
            alignItems: "center",
            boxSizing: "border-box",
            height: boxHeight,
            cursor: "crosshair",
            transition: "background-color 0.4s, padding .3s",
            "&:hover": {
              backgroundColor: perelynBlack,
              color: perelynPrimary,
              "& > *": {
                filter:
                  "brightness(0) saturate(100%)invert(52%) sepia(12%) saturate(2492%) hue-rotate(314deg) brightness(96%) contrast(144%)",
                cursor: "crosshair",
              },
            },
          },
        },
        {
          props: { variant: "transparentSmall" },
          style: {
            backgroundColor: "transparent",
            justifyContent: "center",
            flexDirection: "row",
            alignItems: "center",
            boxSizing: "border-box",
            height: "50%",
            minWidth: "0",
            cursor: "crosshair",
            transition: defaultTransition,
            "&:hover": {
              backgroundColor: perelynPrimary,
            },
          },
        },
        {
          props: { variant: "navigation" },
          style: {
            color: perelynBlack,
            backgroundColor: "transparent",
            border: borderStyle,
            "&:hover": {
              color: perelynBlack,
              backgroundColor: perelynPrimary,
            },
          },
        },
        {
          props: { variant: "orange" },
          style: {
            color: perelynBlack,
            backgroundColor: perelynPrimary,
            "&:hover": {
              color: perelynPrimary,
              backgroundColor: perelynBlack,
            },
            "&:disabled": {
              backgroundColor: perelynGrey,
              color: perelynDarkGrey,
            },
          },
        },
      ],
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          "& .MuiPaper-root": {
            borderRadius: "0",
            border: borderOrange,
            height: "fit-content",
            transition: "all .4s linear",
            color: perelynPrimary,
            boxShadow: "none",
            // "0px 0px 0px 0px rgba(0, 0, 0, 0.2), -1px 1px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          input: {
            "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active":
              {
                webkitBackgroundColor: "text",
                webkitTextFieldColor: "black",
                transition: "background-color 5000s ease-in-out 0s",
                boxShaodw: "inset 0 0 20px 20px transparent",
              },
          },
          "*::-webkit-scrollbar": {
            width: tinyGap,
          },

          "*::-webkit-scrollbar-thumb": {
            backgroundColor: "#ffffff",
            border: borderStyle,
          },
          "& .MuiInputBase-root": {
            borderRadius: "0",
            backgroundColor: "#ffffff",
            border: "none",
            height: mediumTextFieldHeight,
            transition: "all 100ms linear",
          },
          "& .MuiInputBase-multiline": {
            height: "fit-content",
          },
          "& .MuiOutlinedInput-root": {
            transition: "all 100ms linear",

            "& fieldset": {
              transition: "all 100ms linear",
              border: borderStyle,
            },
            "&:hover fieldset": {
              transition: "all 100ms linear",
              border: `1px solid ${perelynPrimary}`,
            },
            "&.Mui-focused fieldset": {
              transition: "all 100ms linear",
              border: `1px solid ${perelynPrimary}`,
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          height: "2.25rem",
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          borderRadius: "40px",
        },
      },
    },
  },
});
